// components/CreateLineInfoModal.jsx
import React, { useState } from "react";
import { X } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateLineInfoModal = ({ account, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    accountId: account?.id || "",
    liRef: "",
    lineType: "--None--",
    sipNumber: "",
    usage: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lineTypeOptions = ["--None--", "Analog", "ISDN", "Other"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = {
        ...formData,
        lineType: formData.lineType === "--None--" ? null : formData.lineType,
      };

      await apiService.createLineInfo(submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating line info:", err);
      setError(
        err.response?.data?.message || "Failed to create line information",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Theme helpers
  const inputClass = `w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDarkMode
      ? "bg-[#0C0E12] border-gray-700 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `text-sm pt-2 ${isDarkMode ? "text-gray-400" : "text-gray-700"}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode
              ? "border-gray-700 bg-[#13161B]"
              : "border-gray-200 bg-white"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            New Line Information
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div
              className={`mb-4 p-3 border rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/20 border-red-500/30 text-red-400"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} id="lineInfoForm">
            {/* Information Section */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-50"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Information
              </h3>
            </div>

            <div className="space-y-4">
              {/* LI Ref */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>LI Ref</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.liRef}
                    onChange={(e) => handleChange("liRef", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Account */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>
                  <span className="text-red-500">*</span> Account
                </label>
                <div className="col-span-2 flex items-center gap-2">
                  <div
                    className={`flex items-center gap-2 px-3 py-2 border rounded flex-1 ${
                      isDarkMode
                        ? "bg-blue-500/20 border-blue-500/30"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
                    <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
                      </svg>
                    </div>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {account?.name || ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`px-3 py-2 text-sm border rounded transition-colors ${
                      isDarkMode
                        ? "text-gray-300 hover:bg-gray-700 border-gray-700"
                        : "text-gray-700 hover:bg-gray-100 border-gray-300"
                    }`}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Line Type */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Line Type</label>
                <div className="col-span-2">
                  <div className="relative">
                    <select
                      value={formData.lineType}
                      onChange={(e) => handleChange("lineType", e.target.value)}
                      className={`${inputClass} appearance-none`}
                    >
                      {lineTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    {lineTypeOptions.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleChange("lineType", type)}
                        className="text-xs text-blue-500 hover:underline"
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* SIP Number */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>SIP Number</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.sipNumber}
                    onChange={(e) => handleChange("sipNumber", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Usage */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className={labelClass}>Usage</label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.usage}
                    onChange={(e) => handleChange("usage", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Save & New
          </button>
          <button
            type="submit"
            form="lineInfoForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>

        {/* Required Information Notice */}
        <div
          className={`px-6 py-2 border-t ${
            isDarkMode
              ? "bg-gray-800/30 border-gray-700"
              : "bg-blue-50 border-blue-100"
          }`}
        >
          <p
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            <span className="text-red-500">*</span> = Required Information
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateLineInfoModal;
